import { Add, Delete, Edit } from '@mui/icons-material'
import {
    Backdrop,
    Portal,
    SpeedDial,
    SpeedDialAction,
    SpeedDialIcon,
} from '@mui/material'
import _ from 'lodash'
import {
    createContext,
    useCallback,
    useContext,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from 'react'
import { Outlet, useLinkClickHandler, useParams } from 'react-router-dom'

import useUserInGroups, {
    COGNITO_GROUP_ADMINS,
} from '../hooks/use-user-in-groups'

// `AdminCUDMenu` instances can be nested inside one another via the router's
// `<Outlet/>` chain (e.g. the photo-gallery album menu wraps a per-photo
// menu). Every instance portals a SpeedDial into the same `#admin-menu` DOM
// node, so without coordination, nested instances would render two
// overlapping FABs. This registry implements "deepest-instance-wins":
// - `AdminCUDMenuDepthContext` tracks how many `AdminCUDMenu` ancestors wrap
//   the current point in the tree (0 at the root, incremented by each
//   instance for its own `<Outlet/>` subtree). This is a *structural*
//   property derived from render position, not mount order, so it's stable
//   regardless of whether nested instances happen to mount in the same
//   commit as their ancestor (React fires mount effects child-first, so
//   mount-order-based registration would get this backwards).
// - `AdminCUDMenuRegistryContext` (provided once, near `#admin-menu` in
//   `App.js`) holds the set of currently-mounted instances and exposes which
//   one is deepest — that's the only one allowed to render its SpeedDial.
const AdminCUDMenuDepthContext = createContext(0)
const AdminCUDMenuRegistryContext = createContext(null)

export const AdminCUDMenuProvider = ({ children }) => {
    const [instances, setInstances] = useState([])
    const nextInstanceId = useRef(0)

    const register = useCallback(depth => {
        const id = nextInstanceId.current++
        setInstances(prev => [...prev, { id, depth }])
        return id
    }, [])

    const unregister = useCallback(id => {
        setInstances(prev => _.reject(prev, { id }))
    }, [])

    const activeId = useMemo(() => {
        if (_.isEmpty(instances)) {
            return null
        }
        const maxDepth = _.maxBy(instances, 'depth').depth
        return _.last(_.filter(instances, { depth: maxDepth })).id
    }, [instances])

    const value = useMemo(
        () => ({ register, unregister, activeId }),
        [register, unregister, activeId]
    )

    return (
        <AdminCUDMenuRegistryContext.Provider value={value}>
            {children}
        </AdminCUDMenuRegistryContext.Provider>
    )
}

const AdminCUDMenu = ({ param }) => {
    const { [param]: id } = useParams()

    const [open, setOpen] = useState(false)
    const isAdmin = useUserInGroups(COGNITO_GROUP_ADMINS)

    const depth = useContext(AdminCUDMenuDepthContext)
    const registry = useContext(AdminCUDMenuRegistryContext)
    const instanceIdRef = useRef(null)

    useLayoutEffect(() => {
        if (_.isNil(registry)) {
            return undefined
        }
        const instanceId = registry.register(depth)
        instanceIdRef.current = instanceId
        return () => registry.unregister(instanceId)
        // Register exactly once per mount/unmount - `depth` is stable for
        // the lifetime of a given instance (it comes from this instance's
        // fixed position in the route tree).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isActive =
        _.isNil(registry) || registry.activeId === instanceIdRef.current

    const handleAddClick = useLinkClickHandler('add')
    const handleEditClick = useLinkClickHandler(`${id}/edit`)
    const handleDeleteClick = useLinkClickHandler(`${id}/delete`)

    const handleOpen = () => setOpen(true)
    const handleClose = () => setOpen(false)

    const adminMenuEl = document.getElementById('admin-menu')

    const actions = [
        { icon: <Add />, title: 'Add', handleClick: handleAddClick },
    ]

    if (!_.isUndefined(id)) {
        actions.push({
            icon: <Edit />,
            title: 'Edit',
            handleClick: handleEditClick,
        })
        actions.push({
            icon: <Delete />,
            title: 'Delete',
            handleClick: handleDeleteClick,
        })
    }

    return (
        <AdminCUDMenuDepthContext.Provider value={depth + 1}>
            <Outlet />
            <Backdrop open={open} />
            {isAdmin && isActive && (
                <Portal container={adminMenuEl}>
                    <SpeedDial
                        data-test-menu="admin"
                        ariaLabel="Admin Menu"
                        sx={{
                            position: 'absolute',
                            bottom: theme => theme.spacing(3),
                            right: theme => theme.spacing(3),
                        }}
                        icon={<SpeedDialIcon />}
                        onClose={handleClose}
                        onOpen={handleOpen}
                        open={open}
                    >
                        {_.map(actions, action => (
                            <SpeedDialAction
                                data-test-action={_.snakeCase(action.title)}
                                key={action.title}
                                icon={action.icon}
                                tooltipTitle={action.title}
                                tooltipOpen
                                onClick={event => {
                                    setOpen(false)
                                    if (!event.defaultPrevented) {
                                        action.handleClick(event)
                                    }
                                }}
                            />
                        ))}
                    </SpeedDial>
                </Portal>
            )}
        </AdminCUDMenuDepthContext.Provider>
    )
}

export default AdminCUDMenu
